<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import { addSizeToDataLayer } from "@/functions/analytics";
import DimensionInput from "@/components/UI/DimensionInput.vue";
import ShapeInputFieldsWrapper from "@/components/UI/ShapeInputFieldsWrapper.vue";
import {Props} from "@/components/ShapeType.vue";

const store = useStore();
const { isCutout, action, prefix, modifierAction } = defineProps<Props>();

const dimensions = computed(() => store.getters.getMeasurements( 'star', isCutout ) );
const modifiers = computed(() => store.getters.getModifiers( isCutout ));
const dimensionLabel = computed(() => store.getters.dimensionLabel);
const dimensionMaxLabel = computed(() => store.getters.getMaxLabelForDimension);
const isDimensionValid = computed(() => store.getters.isShapeValid( isCutout ) );

const innerRadius = computed({
	get: () => dimensions.value.inner_radius,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "inner_radius",
			value: value,
		});
	},
});

const outerRadius = computed({
	get: () => dimensions.value.outer_radius,
	set: (value: string) => {
		store.dispatch(action, {
			dimension: "outer_radius",
			value: value,
		});
	},
});

const numberOfPoints = computed({
	get: () => modifiers.value.number_of_points,
	set: (value: number) => {
		store.commit(modifierAction, {
			modifier: "number_of_points",
			value: value,
		});
	},
});
</script>

<template>
	<shape-input-fields-wrapper>
		<dimension-input
			inputRef="star-inner_radius"
			:id="`${prefix}measurements-star-inner_radius`"
			:name="`${prefix}measurements[dimensions][inner_radius]`"
			unit="cm"
			:label="dimensionLabel('inner_radius', 'star')"
			:instructions="isCutout ? '' : dimensionMaxLabel('inner_radius')"
			:invalid="!isDimensionValid(`${prefix}star-inner_radius`)"
			:onBlur="() => addSizeToDataLayer('inner_radius', +innerRadius, 'star')"
			v-model="innerRadius"
		/>

		<dimension-input
			inputRef="star-outer_radius"
			:id="`${prefix}measurements-star-outer_radius`"
			:name="`${prefix}measurements[dimensions][outer_radius]`"
			unit="cm"
			:label="dimensionLabel('outer_radius', 'star')"
			:instructions="isCutout ? '' : dimensionMaxLabel('outer_radius')"
			:invalid="!isDimensionValid(`${prefix}star-outer_radius`)"
			:onBlur="() => addSizeToDataLayer('outer_radius', +outerRadius, 'star')"
			v-model="outerRadius"
		/>

		<dimension-input
			inputRef="star-number_of_points"
			:id="`${prefix}measurements-star-number_of_points`"
			:name="`${prefix}measurements[modifiers][number_of_points]`"
			inputmode="numeric"
			:label="dimensionLabel('number_of_points', 'star')"
			:invalid="!isDimensionValid(`${prefix}star-number_of_points`)"
			:onBlur="() => addSizeToDataLayer('number_of_points', +numberOfPoints, 'star')"
			v-model="numberOfPoints"
			:step="1"
		/>
	</shape-input-fields-wrapper>
</template>
